/**
 * The bulk disposition (D-28, Dominique Genua 2026-10-02): send a whole set of
 * stale unassigned leads back to the provider in one governed action.
 *
 * The 2026-10-01 packet, section 23: "Bulk action must show: number selected,
 * resulting action, exclusions, confirmation. No generic delete." So the act
 * is never one click. The bar names the count and the action; pressing it
 * opens a panel that asks the server what WOULD happen (nothing is written),
 * says what will be left alone and why, how old the work is, and asks for one
 * reason that stays on every record. Only then does it run.
 *
 * It runs every selected id, not only the ones the preview said would move:
 * the server re-checks each under its own lock. Batches go one at a time with
 * one idempotency key each (`${runKey}-${index}`), so a batch that is retried,
 * or resumed after a stop, is the same act and is never done twice.
 *
 * A case the server recorded FAILED (a transient error, not a refusal) is not
 * an answer. Sending its batch again with the same key retries exactly those
 * cases on the server, and the rest replay as recorded; the panel does that
 * once by itself, and then offers "Retry the N that failed" (independent
 * verification, 2026-10-02).
 *
 * Restraint, on purpose: one accent for the act, a word for every state.
 */
import React, { useRef, useState } from 'react';
import {
  DISPOSITION_WORDS, actKey, refusalWords, type DispositionWord,
} from './resubmittalsApi';
import {
  BULK_BATCH, DEFAULT_BULK_WORD, batches, skipWords, verificationApi, type AgeBands, type BulkOutcome,
} from './verificationApi';

type Phase = 'idle' | 'checking' | 'confirm' | 'running' | 'stopped' | 'finished';
type Left = { case_id: string; code: string };
type Checked = { selected: number; would: number; excluded: Record<string, number>; bands: AgeBands };

const BAND_WORDS: Array<[keyof AgeBands, string]> = [
  ['over_1_year', 'over a year'],
  ['six_to_12_months', '6-12 months'],
  ['days_45_to_6_months', '45 days-6 months'],
  ['under_45_days', 'under 45 days'],
  ['unknown', 'unknown'],
];
const EMPTY_BANDS: AgeBands = { over_1_year: 0, six_to_12_months: 0, days_45_to_6_months: 0, under_45_days: 0, unknown: 0 };
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const countsWords = (counts: Record<string, number>) => Object.entries(counts).filter(([, n]) => n > 0).map(([code, n]) => `${skipWords(code)}: ${n}`);

export default function BulkDisposition({ count, resolveIds, labelFor, onFinished, onClear }: {
  count: number;
  /** The ids to act on, resolved when the panel opens (a filter's "all matching" is asked of the server then). */
  resolveIds: () => Promise<string[]>;
  labelFor?: (caseId: string) => string | undefined;
  onFinished: (summary: string) => void | Promise<void>;
  onClear?: () => void;
}) {
  const [word, setWord] = useState<DispositionWord>(DEFAULT_BULK_WORD);
  const [phase, setPhase] = useState<Phase>('idle');
  const [checked, setChecked] = useState<Checked | null>(null);
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [progress, setProgress] = useState({ k: 0, m: 0, done: 0, left: 0 });
  const [summary, setSummary] = useState<string | null>(null);
  const [leftRows, setLeftRows] = useState<Left[]>([]);
  const [stoppedAt, setStoppedAt] = useState<{ k: number; m: number } | null>(null);
  // What a resumed run needs, kept outside render state so a retry never reads a stale closure.
  const run = useRef<{ key: string; batches: string[][]; next: number; outcomes: Array<BulkOutcome | undefined>; word: DispositionWord; reason: string }>({
    key: '', batches: [], next: 0, outcomes: [], word: DEFAULT_BULK_WORD, reason: '',
  });
  const [failedCount, setFailedCount] = useState(0);

  const chosen = DISPOSITION_WORDS.find((d) => d.word === word)!;
  const toProvider = chosen.toProvider;
  const toVerify = Boolean(chosen.toVerify);
  const actionSentence = toProvider
    ? `Each will close as ${chosen.label} and go on Resubmittals, once per resident, for the provider's next visit.`
    : toVerify
      ? 'Each will close and go on Needs verification, once per resident, for a VA to check benefits.'
      : word === 'DNQ'
        ? 'Each will close as Does not qualify; the 6-month re-check applies.'
        : `Each will close as ${chosen.label}.`;
  const verbPhrase = toProvider ? 'sent back to the provider' : toVerify ? 'closed and sent to Needs verification' : `closed as ${chosen.label}`;
  const buttonText = toProvider ? 'Send back to the provider…' : `Close as ${chosen.label}…`;
  const busy = phase === 'checking' || phase === 'running';

  const closePanel = () => { setPhase('idle'); setChecked(null); setReason(''); setTried(false); setProblem(null); };

  const open = async () => {
    setProblem(null); setSummary(null); setLeftRows([]); setTried(false); setReason('');
    setPhase('checking');
    try {
      const ids = Array.from(new Set(await resolveIds()));
      const agg: Checked = { selected: ids.length, would: 0, excluded: {}, bands: { ...EMPTY_BANDS } };
      for (const part of batches(ids, BULK_BATCH)) {
        const r = await verificationApi.preview(part, word);
        if (!r.success || !r.data) { setProblem(refusalWords(r, 'eMerge could not check this selection. Nothing was changed.')); setPhase('idle'); return; }
        agg.would += r.data.would.length;
        for (const [code, n] of Object.entries(r.data.excluded_counts || {})) agg.excluded[code] = (agg.excluded[code] || 0) + (n || 0);
        for (const k of Object.keys(EMPTY_BANDS) as Array<keyof AgeBands>) agg.bands[k] += r.data.age_bands?.[k] || 0;
      }
      run.current = { key: actKey(), batches: batches(ids, BULK_BATCH), next: 0, outcomes: [], word, reason: '' };
      setChecked(agg); setPhase('confirm');
    } catch { setProblem('Could not reach eMerge. Nothing was changed.'); setPhase('idle'); }
  };

  /** One batch, with one retry on the same key when the server was never reached or failed (5xx). */
  const sendBatch = async (i: number): Promise<{ ok: true; out: BulkOutcome } | { ok: false; why: string }> => {
    const r0 = run.current;
    let why = '';
    for (let attempt = 0; attempt < 2; attempt += 1) {
      try {
        const r = await verificationApi.run(r0.batches[i], r0.word, r0.reason, `${r0.key}-${i}`);
        if (r.success && r.data) return { ok: true, out: r.data };
        if (r.status !== undefined && r.status < 500) return { ok: false, why: refusalWords(r, 'eMerge refused this batch.') };
        why = refusalWords(r, 'eMerge failed on this batch.');
      } catch { why = 'Could not reach eMerge.'; }
    }
    return { ok: false, why };
  };

  /** Totals over every batch's latest answer (a resent batch's answer replaces its earlier one). */
  const totals = () => {
    const outs = run.current.outcomes.filter(Boolean) as BulkOutcome[];
    return { done: outs.reduce((n, o) => n + o.done.length, 0), left: outs.flatMap((o) => o.skipped) };
  };
  const failedBatches = () => run.current.outcomes.map((o, i) => (o && o.skipped.some((s) => s.code === 'FAILED') ? i : -1)).filter((i) => i >= 0);

  const finish = async () => {
    const t = totals();
    const counts: Record<string, number> = {};
    for (const s of t.left) counts[s.code] = (counts[s.code] || 0) + 1;
    const sentence = `${t.done} ${verbPhrase}.`;
    const left = t.left.length ? ` ${t.left.length} left alone: ${countsWords(counts).join('; ')}.` : '';
    const text = `${sentence}${left}`;
    setFailedCount(counts.FAILED || 0);
    setSummary(text); setLeftRows([...t.left]); setPhase('finished');
    await onFinished(text);
  };

  /** Send batch i (same key, same ids) and keep its answer; false when it could not be sent. */
  const sendAndKeep = async (i: number): Promise<boolean> => {
    const r = run.current;
    const res = await sendBatch(i);
    if (!res.ok) { setStoppedAt({ k: i + 1, m: r.batches.length }); setProblem(res.why); setPhase('stopped'); return false; }
    r.outcomes[i] = res.out;
    const t = totals();
    setProgress({ k: i + 1, m: r.batches.length, done: t.done, left: t.left.length });
    return true;
  };

  const loop = async () => {
    const r = run.current;
    setPhase('running'); setStoppedAt(null); setProblem(null);
    for (let i = r.next; i < r.batches.length; i += 1) {
      const t = totals();
      setProgress({ k: i + 1, m: r.batches.length, done: t.done, left: t.left.length });
      if (!(await sendAndKeep(i))) return;
      r.next = i + 1;
    }
    // Cases the server recorded FAILED are not answers: resend their batches once (the server retries only those).
    for (const i of failedBatches()) {
      if (!(await sendAndKeep(i))) return;
    }
    await finish();
  };

  const retryFailed = async () => {
    setPhase('running'); setProblem(null);
    for (const i of failedBatches()) {
      if (!(await sendAndKeep(i))) return;
    }
    await finish();
  };

  const confirm = (e: React.FormEvent) => {
    e.preventDefault();
    if (!checked || checked.would === 0 || busy) return;
    if (!reason.trim()) { setTried(true); return; }
    run.current.reason = reason.trim();
    void loop();
  };

  if (count === 0 && phase === 'idle' && !summary) return null;
  const missing = tried && !reason.trim();

  return (
    <section className="bd" aria-label="Bulk action on the selected leads">
      {(count > 0 || phase !== 'idle') && phase !== 'finished' && (
        <div className="bd-bar">
          {count > 0 && <strong>{count} selected</strong>}
          <select value={word} onChange={(e) => { setWord(e.target.value as DispositionWord); closePanel(); }} aria-label="What to do with the selected leads" disabled={busy || phase === 'stopped'}>
            {DISPOSITION_WORDS.map((d) => <option key={d.word} value={d.word}>{d.label}</option>)}
          </select>
          {phase === 'idle' && <button type="button" className="mf-go" onClick={open} disabled={count === 0}>{buttonText}</button>}
          {onClear && count > 0 && phase === 'idle' && <button type="button" className="rs-quiet" onClick={onClear}>Clear selection</button>}
          {phase === 'checking' && <span role="status" className="mf-sub">Checking…</span>}
        </div>
      )}
      {problem && phase === 'idle' && <p className="rs-alert" role="alert">{problem}</p>}

      {phase === 'confirm' && checked && (
        <form className="bd-panel" onSubmit={confirm} aria-label="Confirm the bulk action">
          <p><strong>Selected: {checked.selected}.</strong> {actionSentence}</p>
          {checked.would === 0
            ? <p className="mf-note warn">Nothing will move: every selected lead would be left alone.</p>
            : <p><strong>{checked.would}</strong> will move.</p>}
          {Object.values(checked.excluded).some((n) => n > 0) && (
            <div>
              <p className="mf-sub">Left alone:</p>
              <ul className="bd-list" aria-label="Left alone">
                {countsWords(checked.excluded).map((w) => <li key={w}>{cap(w)}</li>)}
              </ul>
            </div>
          )}
          {checked.would > 0 && (
            <p className="mf-sub">
              Age of what will move (last placed, else opened):{' '}
              {BAND_WORDS.filter(([k]) => checked.bands[k] > 0).map(([k, w]) => `${checked.bands[k]} ${w}`).join(' · ') || 'not recorded'}
            </p>
          )}
          <label className="bd-why">
            <span>Why (required). One reason covers every lead in this batch, and it stays on each record.</span>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} aria-label="Reason for the bulk action"
              aria-required aria-invalid={missing ? true : undefined} aria-describedby={missing ? 'bd-why-missing' : undefined} />
          </label>
          {missing && <p id="bd-why-missing" className="rs-alert" role="alert">Say why. One reason covers every lead in this batch, and it stays on each record.</p>}
          <span className="rs-acts">
            <button type="submit" className="mf-go" disabled={checked.would === 0}>{toProvider ? `Send back ${checked.would}` : `Close ${checked.would}`}</button>
            <button type="button" className="mf-go ghost" onClick={closePanel}>Cancel</button>
          </span>
        </form>
      )}

      {(phase === 'running' || phase === 'stopped') && (
        <div className="bd-panel">
          <p role="status">Batch {progress.k} of {progress.m} · {progress.done} done · {progress.left} left alone</p>
          {phase === 'stopped' && stoppedAt && (
            <>
              <p className="rs-alert" role="alert">
                Stopped at batch {stoppedAt.k} of {stoppedAt.m}. Nothing after it was sent. Resume continues with the same keys, so nothing is done twice.
                {problem ? ` (${problem})` : ''}
              </p>
              <span className="rs-acts"><button type="button" className="mf-go" onClick={() => { void loop(); }}>Resume</button></span>
            </>
          )}
        </div>
      )}

      {phase === 'finished' && summary && (
        <div className="bd-panel">
          <p role="status">{summary}</p>
          {leftRows.length > 0 && (
            <details className="bd-details">
              <summary>Show the {leftRows.length} left alone</summary>
              <ul className="bd-list">
                {leftRows.map((s) => <li key={s.case_id}>{(labelFor && labelFor(s.case_id)) || 'A resident not on this page'}: {skipWords(s.code)}</li>)}
              </ul>
            </details>
          )}
          {failedCount > 0 && (
            <p className="rs-alert" role="alert">
              {failedCount === 1 ? 'One lead' : `${failedCount} leads`} failed on eMerge&apos;s side and {failedCount === 1 ? 'was' : 'were'} not changed. Retrying sends the same batches again; nothing already done is done twice.
            </p>
          )}
          <span className="rs-acts">
            {failedCount > 0 && <button type="button" className="mf-go" onClick={() => { void retryFailed(); }}>Retry the {failedCount} that failed</button>}
            <button type="button" className="mf-go ghost" onClick={() => { setSummary(null); setLeftRows([]); setFailedCount(0); setPhase('idle'); }}>Done</button>
          </span>
        </div>
      )}
    </section>
  );
}
